import React, { useEffect, useMemo, useState } from 'react';
import { Image, Popover } from 'antd';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { ImageStyles } from './ImageAdded.styles';
import {
  ExportOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  HighlightOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
// @ts-ignore
import exifr from 'exifr/dist/lite.esm.js';
import formatDateTime from '../../utils/formatDateTime';
import LoadingIcon from '../shared/LoadingIcon';
import { DamageImageData } from '../../@types';
import { updateDamageImageVisibility } from '../../redux/store/userData/claims';
import { selectAuth } from '../../redux/store/userData/user';
import {
  EditingClaimSource,
  openModal,
  setEditingClaim,
} from '../../redux/store/ui/common';

type ImageAddedProps = {
  vehicleEvent: any;
  unlimited?: boolean;
  damageId?: string;
  imageMeta?: DamageImageData[];
  source?: EditingClaimSource;
};

function forceDownload(blob: string, filename: string) {
  var a = document.createElement('a');
  a.download = filename;
  a.href = blob;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Current blob size limit is around 500MB for browsers
function downloadResource(url: any, filename: string) {
  if (!filename) filename = url?.split('\\')?.pop()?.split('/')?.pop();
  fetch(url, {
    headers: new Headers({
      Origin: document.location.origin,
      'Cache-Control': 'no-cache',
    }),
    mode: 'cors',
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Error fetching file: ${response.statusText}`);
      }
      const blob = await response.blob();
      const sniffed = await sniffMimeFromBlob(blob);
      const resolvedType = sniffed || response.headers.get('Content-Type');
      const extension = getFileExtensionFromContentType(resolvedType);
      if (!filename.includes('.')) {
        filename += extension; // Add the extension if missing
      }
      return blob;
    })
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob);
      forceDownload(blobUrl, filename);
    })
    .catch((e) => console.error('Download failed:', e));
}

async function sniffMimeFromBlob(blob: Blob): Promise<string | null> {
  try {
    const buf = await blob.slice(0, 12).arrayBuffer();
    const a = new Uint8Array(buf);
    if (a[0] === 0x25 && a[1] === 0x50 && a[2] === 0x44 && a[3] === 0x46) {
      return 'application/pdf';
    }
    if (a[0] === 0x89 && a[1] === 0x50 && a[2] === 0x4e && a[3] === 0x47) {
      return 'image/png';
    }
    if (a[0] === 0xff && a[1] === 0xd8 && a[2] === 0xff) {
      return 'image/jpeg';
    }
    if (a[0] === 0x47 && a[1] === 0x49 && a[2] === 0x46 && a[3] === 0x38) {
      return 'image/gif';
    }
    if (
      a[0] === 0x52 &&
      a[1] === 0x49 &&
      a[2] === 0x46 &&
      a[3] === 0x46 &&
      a[8] === 0x57 &&
      a[9] === 0x45 &&
      a[10] === 0x42 &&
      a[11] === 0x50
    ) {
      return 'image/webp';
    }
    return null;
  } catch {
    return null;
  }
}

function getFileExtensionFromContentType(contentType) {
  const ct = (contentType || '').toLowerCase().split(';')[0].trim();
  switch (ct) {
    case 'image/jpeg':
      return '.jpeg';
    case 'image/jpg':
      return '.jpg';
    case 'image/png':
      return '.png';
    case 'image/gif':
      return '.gif';
    case 'image/webp':
      return '.webp';
    case 'image/svg+xml':
      return '.svg';
    case 'application/pdf':
      return '.pdf';
    case 'application/msword':
      return '.doc';
    case 'text/plain':
      return '.txt';
    case 'application/zip':
      return '.zip';
    case 'application/x-rar-compressed':
      return '.rar';
    case 'application/x-7z-compressed':
      return '.7z';
    case 'application/json':
      return '.json';
    default:
      return '.png';
  }
}

async function fetchImageMetadata(imageURL) {
  try {
    const response = await fetch(imageURL, {
      headers: new Headers({
        Origin: document.location.origin,
        'Cache-Control': 'no-cache',
      }),
      mode: 'cors',
    });
    if (response.ok) {
      const blob = await response.blob();
      return exifr.parse(blob, { reviveValues: false });
    }
  } catch (error) {
    // console.error(error);
  }
}

export async function getFileContentType(
  fileUrl: string
): Promise<string | null> {
  try {
    const response = await fetch(fileUrl, {
      headers: new Headers({
        Origin: document.location.origin,
        'Cache-Control': 'no-cache',
      }),
      mode: 'cors',
    });
    if (!response.ok) {
      console.warn(`Error fetching file: ${response.statusText}`);
      return null;
    }
    const blob = await response.blob();
    const sniffed = await sniffMimeFromBlob(blob);
    return sniffed || response.headers.get('Content-Type');
  } catch (error) {
    console.warn('Failed to fetch file content type:', error);
    return null;
  }
}

const ImageAdded: React.FC<ImageAddedProps> = ({
  vehicleEvent: ve,
  unlimited = false,
  damageId,
  imageMeta,
  source = 'claim-drawer',
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const isClaimsManager = !!auth?.permissions?.claimsManager;
  const [currentImageMetadata, setCurrentImageMetadata] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [fileType, setFileType] = useState('');
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const images = ve.images || [];
  const imagesObj = Object.assign({}, images);
  const displayedImages = unlimited ? images : images.slice(-4);
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px',
    ...(unlimited ? { flexWrap: 'wrap', rowGap: '8px' } : {}),
  };

  // Keyed by url: `images[].url` is the same string as its `damageImages`
  // entry, but the two arrays aren't guaranteed to be in the same order.
  const metaByUrl = useMemo(() => {
    const map = new Map<string, DamageImageData>();
    (imageMeta || []).forEach((meta) => {
      if (meta?.url) map.set(meta.url, meta);
    });
    return map;
  }, [imageMeta]);
  const canToggleVisibility = !!damageId && isClaimsManager;

  const getImageMeta = (file: string): DamageImageData | undefined =>
    metaByUrl.get(file);

  const buildFileName = (file: string, index: number) =>
    ve?.plate
      ? `${ve?.plate}_${moment(ve?.addedAt).format('YYYY-MM-DD_HH:mm')}_${
          ve?.presentableTripId || ve?.tripPresentableId
        }_${
          ve?.imageType === 'rentalStartImage'
            ? `START_${index + 1}`
            : ve?.imageType === 'rentalEndImage'
            ? `END_${index + 1}`
            : ve?.imageType === 'serviceTripImage'
            ? `SERVICE_${index + 1}`
            : `${ve.damageId}_${index + 1}`
        }`
      : 'image';

  const onToggleVisibility = (meta: DamageImageData) => {
    setOpenMenuIndex(null);
    dispatch(
      updateDamageImageVisibility(damageId!, meta.key!, !meta.hiddenFromUser)
    );
  };

  const onAnnotate = (meta: DamageImageData) => {
    setOpenMenuIndex(null);
    batch(() => {
      dispatch(
        setEditingClaim({
          claim: { ...ve, id: ve?.id ?? damageId },
          source,
          image: meta,
        })
      );
      dispatch(openModal('ClaimImageAnnotateModal'));
    });
  };

  const renderImageActions = (file: string, index: number) => {
    const meta = getImageMeta(file);
    const isHidden = !!meta?.hiddenFromUser;
    const showToggle = canToggleVisibility && !!meta?.key;

    if (!showToggle) {
      return (
        <div
          className="image-actions"
          data-testid={`ImageAdded-download-${index}`}
          onClick={() => downloadResource(file, buildFileName(file, index))}
        >
          <DownloadOutlined style={{ fontSize: '16px' }} />
        </div>
      );
    }

    const content = (
      <div className="image-actions-menu">
        <div
          className="image-actions-menu-item"
          data-testid={`ImageAdded-download-${index}`}
          onClick={() => {
            setOpenMenuIndex(null);
            downloadResource(file, buildFileName(file, index));
          }}
        >
          <DownloadOutlined style={{ fontSize: '16px' }} />
          <span>
            {intl.formatMessage({
              id: 'fleetadmin.claim.image.download',
              defaultMessage: 'Download',
            })}
          </span>
        </div>
        <div
          className="image-actions-menu-item"
          data-testid={`ImageAdded-annotate-${index}`}
          onClick={() => onAnnotate(meta!)}
        >
          <HighlightOutlined style={{ fontSize: '16px' }} />
          <span>
            {intl.formatMessage({
              id: 'fleetadmin.claim.image.annotate',
              defaultMessage: 'Annotate',
            })}
          </span>
        </div>
        <div
          className="image-actions-menu-item"
          data-testid={`ImageAdded-toggleVisibility-${index}`}
          onClick={() => onToggleVisibility(meta!)}
        >
          {isHidden ? (
            <VisibilityIcon style={{ fontSize: '16px' }} />
          ) : (
            <VisibilityOffIcon style={{ fontSize: '16px' }} />
          )}
          <span>
            {isHidden
              ? intl.formatMessage({
                  id: 'fleetadmin.claim.image.unhide',
                  defaultMessage: 'Unhide from user',
                })
              : intl.formatMessage({
                  id: 'fleetadmin.claim.image.hide',
                  defaultMessage: 'Hide from user',
                })}
          </span>
        </div>
      </div>
    );

    return (
      <Popover
        content={content}
        trigger="click"
        placement="bottomRight"
        overlayClassName="image-actions-popover"
        open={openMenuIndex === index}
        onOpenChange={(open) => setOpenMenuIndex(open ? index : null)}
      >
        <div
          className="image-actions"
          data-testid={`ImageAdded-actions-${index}`}
        >
          <MoreOutlined style={{ fontSize: '16px' }} />
        </div>
      </Popover>
    );
  };

  useEffect(() => {
    if (currentImageIndex !== null && !currentImageMetadata) {
      setLoadingMeta(true);
      fetchImageMetadata(images[currentImageIndex])
        .then((meta) => {
          console.log(meta);
          setLoadingMeta(false);
          setCurrentImageMetadata(meta);
        })
        .catch((error) => {
          setLoadingMeta(false);
          console.error('Failed to fetch image metadata', error);
        });
    }
  }, [currentImageIndex]);

  useEffect(() => {
    async function fetchType() {
      try {
        if (!ve.images || ve.images.length === 0) return;
        const contentType = await getFileContentType(ve.images[0]);
        if (contentType) {
          const extension = getFileExtensionFromContentType(contentType);
          setFileType(extension);
        }
      } catch {
        // Silently handle fetch errors
      }
    }
    fetchType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ve.images?.[0]]);

  if (!images.length) return null;

  const CustomToolbar = (originalNode, info) => {
    const dateTime = currentImageMetadata?.['CreateDate']?.split(' ');
    const date = dateTime?.[0]?.replaceAll(':', '-');
    const isoCreation = `${date}T${dateTime?.[1]}${currentImageMetadata?.['OffsetTime']}`;
    const metaDate = moment(isoCreation).tz('Europe/Berlin');

    return (
      <div>
        {loadingMeta && (
          <div className="image-preview-meta">
            <LoadingIcon color="white" />
          </div>
        )}
        {!loadingMeta && currentImageMetadata && (
          <div className="image-preview-meta">
            {`${
              metaDate ? formatDateTime(metaDate, 'YYYY-MM-DD HH:mm:ss') : ''
            } | ${currentImageMetadata?.Make || ''} (${
              currentImageMetadata?.Model || ''
            })`}
          </div>
        )}
        {originalNode}
      </div>
    );
  };

  if (fileType === '.pdf') {
    return (
      <>
        <div style={rowStyle}>
          {displayedImages.map((file) => (
            <div key={file} style={{ display: 'flex', alignItems: 'center' }}>
              <div
                onClick={() => downloadResource(file, 'pdf')}
                style={{
                  width: 50,
                  height: 50,
                  marginLeft: '4px',
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  background: '#fafafa',
                }}
              >
                <FilePdfOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
              </div>
              <Link
                replace={false}
                to={{
                  pathname: `https://drive.google.com/viewer?url=${file}&embedded=true`,
                }}
                target="_blank"
              >
                <a target="_blank" rel="noreferrer">
                  <ExportOutlined
                    style={{ fontSize: '18px', marginLeft: '10px' }}
                  />
                </a>
              </Link>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <Image.PreviewGroup
        preview={{
          toolbarRender: CustomToolbar,
          onVisibleChange: (visible, prevValue, current) => {
            if (visible) {
              setCurrentImageIndex(current);
            } else {
              setCurrentImageMetadata(null);
              setCurrentImageIndex(null);
            }
          },
        }}
      >
        <div style={rowStyle}>
          {displayedImages.map((file, index) => (
            <div key={file} style={{ display: 'flex', alignItems: 'center' }}>
              <div className="image-thumb">
                <Image
                  width={50}
                  height={50}
                  src={file}
                  wrapperStyle={{
                    marginLeft: '4px',
                    borderRadius: '8px',
                    ...(getImageMeta(file)?.hiddenFromUser
                      ? { opacity: 0.4 }
                      : {}),
                  }}
                  fallback="data:image/png;base64,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"
                />
                {getImageMeta(file)?.hiddenFromUser && (
                  <div
                    className="image-hidden-badge"
                    title={intl.formatMessage({
                      id: 'fleetadmin.claim.image.hidden',
                      defaultMessage: 'Hidden from user',
                    })}
                  >
                    <VisibilityOffIcon style={{ fontSize: '11px' }} />
                  </div>
                )}
              </div>
              {renderImageActions(file, index)}
            </div>
          ))}
          <Link
            replace={false}
            to={{
              pathname: '/images',
              search: `?images=${JSON.stringify(imagesObj)}`,
            }}
            target="_blank"
          >
            <a target="_blank" rel="noreferrer">
              <ExportOutlined
                style={{ fontSize: '18px', marginLeft: '10px' }}
              />
            </a>
          </Link>
        </div>
      </Image.PreviewGroup>
      <style jsx>{ImageStyles}</style>
    </>
  );
};

export default ImageAdded;
